import useEditOrderContext from './useEditOrderContext';
import { useEffect, useRef, useState } from 'react';

import type { ChangeEvent } from 'react';

type InputElement = HTMLInputElement | HTMLTextAreaElement;

export type Options<ModelValue> = {
	/** Form field name — used for form-level validation tracking */
	name?: string;
	/** Milliseconds to debounce commits. 0 = immediate. Default: 0 */
	debounce?: number;
	/** Convert model value → display string */
	format: (value: ModelValue) => string;
	/** Convert display string → model value */
	parse: (value: string) => ModelValue;
	/** Return true if valid. Called on every commit. */
	validate?: (value: ModelValue) => boolean;
};

export function isEmpty(value: unknown): boolean {
	return value === undefined || value === '' || value === null || value !== value;
}

/**
 * Creates a validator from standard HTML input attributes.
 */
export function createInputValidator<ModelValue = unknown>(props: {
	required?: boolean;
	min?: number | string;
	max?: number | string;
	minLength?: number;
	maxLength?: number;
	pattern?: string;
}): ((value: ModelValue) => boolean) | undefined {
	const checks: ((value: ModelValue) => boolean)[] = [];

	if (props.required) {
		checks.push(value => !isEmpty(value));
	}
	if (props.pattern !== undefined) {
		const regexp = new RegExp(`^${props.pattern}$`);
		checks.push(value => isEmpty(value) || regexp.test(String(value)));
	}
	if (props.minLength !== undefined) {
		const minLength = props.minLength;
		checks.push(value => isEmpty(value) || String(value).length >= minLength);
	}
	if (props.maxLength !== undefined) {
		const maxLength = props.maxLength;
		checks.push(value => isEmpty(value) || String(value).length <= maxLength);
	}
	if (props.min !== undefined) {
		const min = typeof props.min === 'string' ? parseFloat(props.min) : props.min;
		if (!isNaN(min)) {
			checks.push(value => isEmpty(value) || (value as number) >= min);
		}
	}
	if (props.max !== undefined) {
		const max = typeof props.max === 'string' ? parseFloat(props.max) : props.max;
		if (!isNaN(max)) {
			checks.push(value => isEmpty(value) || (value as number) <= max);
		}
	}

	return checks.length > 0 ? value => checks.every(check => check(value)) : undefined;
}

/**
 * Manages the value lifecycle of a form input.
 *
 * Inspired by Angular's ngModelController — separates "setting the view value"
 * (immediate, keeps UI responsive) from "committing" (possibly debounced, updates the model).
 */
const useInputController = <ModelValue>(
	modelValue: ModelValue,
	onChange: ((value: ModelValue) => void) | undefined,
	options: Options<ModelValue>
) => {
	const { registerFormComponent, unregisterFormComponent, onFormConponentChange } = useEditOrderContext();

	const [displayValue, setDisplayValue] = useState(() => options.format(modelValue));

	// Refs ensure commit() always reads fresh values, even from stale closures (setTimeout, flush)
	const displayValueRef = useRef(displayValue);
	const modelValueRef = useRef(modelValue);
	const onChangeRef = useRef(onChange);
	const lastCommittedRef = useRef(modelValue);
	const pendingRef = useRef<NodeJS.Timeout | null>(null);
	const parseRef = useRef(options.parse);
	const validateRef = useRef(options.validate);

	// Sync refs every render
	modelValueRef.current = modelValue;
	onChangeRef.current = onChange;
	parseRef.current = options.parse;
	validateRef.current = options.validate;

	// Like Angular's $commitViewValue — parse display value and notify parent
	function commit() {
		if (pendingRef.current) {
			clearTimeout(pendingRef.current);
			pendingRef.current = null;
		}

		const parsed = parseRef.current(displayValueRef.current);

		if (parsed !== modelValueRef.current) {
			lastCommittedRef.current = parsed;
			onChangeRef.current?.(parsed);

			if (options.name) {
				const valid = validateRef.current?.(parsed) ?? true;
				onFormConponentChange(options.name, valid, true);
			}
		}
	}

	function scheduleCommit() {
		if (pendingRef.current) {
			clearTimeout(pendingRef.current);
		}

		const delay = options.debounce ?? 0;
		if (delay > 0) {
			pendingRef.current = setTimeout(commit, delay);
		} else {
			commit();
		}
	}

	// Sync from parent when model value changes externally (not from our commit)
	useEffect(() => {
		if (modelValue !== lastCommittedRef.current) {
			const formatted = options.format(modelValue);
			displayValueRef.current = formatted;
			setDisplayValue(formatted);

			if (options.name) {
				const valid = options.validate?.(modelValue) ?? true;
				onFormConponentChange(options.name, valid, false);
			}
		}
		lastCommittedRef.current = modelValue;
	}, [modelValue]);

	// Always clean up pending debounce on unmount
	useEffect(() => {
		return () => {
			if (pendingRef.current) {
				clearTimeout(pendingRef.current);
			}
		};
	}, []);

	// Register with form for validation tracking + flush on save
	useEffect(() => {
		if (options.name) {
			const valid = options.validate?.(modelValue) ?? true;
			registerFormComponent(options.name, valid, commit);

			return () => {
				unregisterFormComponent(options.name!);
			};
		}
	}, []);

	return {
		value: displayValue,

		onChange(event: ChangeEvent<InputElement>) {
			const value = event.target.value;
			// Update ref BEFORE scheduling commit — if debounce is 0,
			// commit fires synchronously and needs the new value now
			displayValueRef.current = value;
			setDisplayValue(value);
			scheduleCommit();
		},

		onBlur() {
			// Always commit immediately on blur — flushes any pending debounce
			commit();
		}
	};
};

export default useInputController;
